import { useState, useEffect, useRef } from "react";
import tw from "tailwind-styled-components";
import {
    BoxContainer,
    Underline,
    BoxTitle,
    BoxHead,
    FilterTableWrapper,
    FilterWrapper,
} from "../../Components/Styles/PageStyles";
import SearchFilterBox from "../../Components/SearchFilterBox";
import Table from "../../Components/Deal/ClosedTable";
import SideModal from "../../Components/SideModal";
import EditDeal from "../../Components/Deal/EditDeal";
import EditBasicInfo from "../../Components/Deal/EditBasicInfo";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import axios from "axios";
import Config from "../../Config";
import { useSelector } from "react-redux";
import NotFoundModel from "../../Components/NotFoundModel";
import Loading from "../../Components/Loading";
import Notes from "../../Components/Deal/Note";
import moment from "moment"
import DateRange from "../../Components/DateRange";

const ClosedDeal = () => {
    const user = useSelector((state) => state.UserReducer.user);
    const searchOptions = [{ label: "Name", value: 1 }];
    const [selectedData, setSelectedData] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isEditDeal, setIsEditDeal] = useState(false);
    const [isEditBasicInfo, setIsEditBasicInfo] = useState(false);
    const [apiData, setApiData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [activeTab, setActiveTab] = useState("1");
    const [summaryData, setSummaryData] = useState(null);
    const [isViewNotes, setIsViewNotes] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("");

    //--Date filter---
    const [startDate, setStartDate] = useState(
        moment().startOf("month").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        moment().endOf("month").format("YYYY-MM-DD")
    );

    // Track if filter is applied
    const [isFilterApplied, setIsFilterApplied] = useState(false);

    const getFilterType = (type) => {
        switch (type) {
            case 1:
                return "NAME";
            default:
                return "";
        }
    }

    const fetchDealList = async (params) => {
        const query = new URLSearchParams({
            s: params.searchQuery || "",
            t: params.filterType || "",
            startDate: params.startDate || "",
            endDate: params.endDate || "",
        }).toString();

        const response = await axios.get(
            `${Config.apiUrl}/deal/list/closed?${query}`,
            {
                headers: {
                    authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    const getDealSuccess = (res) => {
        if (res?.isSuccess) {
            setApiData(res?.data || []);
            // Filter data based on active tab after API response
            const filteredByTab = filterByTab(res?.data || [], activeTab);
            setFilteredData(filteredByTab);
            setSummaryData(res?.stats || null);
            setIsFocused(true);
        }
    };

    const getDealError = (err) => {
        toast.error(err?.response?.data?.msg || "Error fetching deals");
    };

    const {
        mutate: getDealListMutate,
        isLoading,
        isRefetching,
        error
    } = useMutation(fetchDealList, {
        onSuccess: getDealSuccess,
        onError: getDealError,
    });

    useEffect(() => {
        getDealListMutate({
            searchQuery,
            filterType,
            startDate,
            endDate,
        });
    }, []);

    // Function to filter data by tab
    const filterByTab = (data, tab) => {
        if (!data) return [];

        switch (tab) {
            case "1": // Cancelled - status 6
                return data.filter(deal => deal.status === 6);
            case "2": // Lost - status 7
                return data.filter(deal => deal.status === 7);
            case "3": // Leased End - status 8
                return data.filter(deal => deal.status === 8);
            default:
                return data;
        }
    };

    const handleFilterChange = (values) => {
        const type = getFilterType(values.filterType);
        setFilterType(type);
        setSearchQuery(values.searchQuery);
        setIsFilterApplied(true); // Mark filter as applied

        getDealListMutate({
            searchQuery: values.searchQuery,
            filterType: type,
            startDate,
            endDate,
        });
    };

    const handleClearFilter = () => {
        setFilterType("");
        setSearchQuery("");
        setIsFilterApplied(false); // Reset filter applied flag
        getDealListMutate({ searchQuery: "", filterType: "" });
    };

    //-----Edit Deal Basic--------------
    const editDealBasicFunction = async (values) => {
        return await axios.post(
            `${Config.apiUrl}/deal/edit/basic`,
            values,
            {
                headers: {
                    "Content-Type": "application/json",
                    authorization: `Bearer ${user.token}`,
                },
            }
        );
    };

    const editDealBasicSuccess = (data) => {
        setIsEditBasicInfo(false);
        getDealListMutate({ searchQuery, filterType });
        toast.success(data?.data?.msg || "Success");
    };

    const editDealBasicError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: editDealBasicLoading, mutate: editDealBasicMutate } =
        useMutation(editDealBasicFunction, {
            onSuccess: editDealBasicSuccess,
            onError: editDealBasicError,
        });

    //-----Edit Deal--------------
    const editDealFunction = async (values) => {
        return await axios.post(
            `${Config.apiUrl}/deal/edit`,
            values,
            {
                headers: {
                    "Content-Type": "application/json",
                    authorization: `Bearer ${user.token}`,
                },
            }
        );
    };

    const editDealSuccess = (data) => {
        setIsEditDeal(false);
        getDealListMutate({ searchQuery, filterType });
        toast.success(data?.data?.msg || "Success");
    };

    const editDealError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: editDealLoading, mutate: editDealMutate } =
        useMutation(editDealFunction, {
            onSuccess: editDealSuccess,
            onError: editDealError,
        });

    // Effect to handle tab changes
    useEffect(() => {
        if (!apiData) return;

        // When tab changes, filter the data based on the selected tab
        const filtered = filterByTab(apiData, activeTab);
        setFilteredData(filtered);
    }, [activeTab, apiData]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedId(null);
        // Reset filter applied flag when changing tabs
        if (isFilterApplied) {
            setIsFilterApplied(false);
            // Clear search filters when changing tabs
            setFilterType("");
            setSearchQuery("");
        }
    };

    const handleDealClose = () => {
        setIsEditDeal(false);
    }

    const handleEditInfoClose = () => {
        setIsEditBasicInfo(false);
    }

    return (
        <>
            <BoxContainer>
                <BoxHead>
                    <BoxTitle className="!mb-0 whitespace-nowrap">
                        Closed Deals
                        <Underline />
                    </BoxTitle>
                </BoxHead>

                <SideModal
                    isOpen={isEditDeal}
                    onClose={() => setIsEditDeal(false)}
                >
                    <EditDeal
                        selectedData={selectedData}
                        handleClose={handleDealClose}
                        editDealLoading={editDealLoading}
                        editDealMutate={editDealMutate}
                    />
                </SideModal>

                <SideModal
                    isOpen={isEditBasicInfo}
                    onClose={() => setIsEditBasicInfo(false)}
                >
                    <EditBasicInfo
                        selectedData={selectedData}
                        handleClose={handleEditInfoClose}
                        editDealBasicLoading={editDealBasicLoading}
                        editDealBasicMutate={editDealBasicMutate}
                    />
                </SideModal>

                <SideModal
                    isOpen={isViewNotes}
                    onClose={() => setIsViewNotes(false)}
                >
                    <Notes
                        isViewNotes={isViewNotes}
                        setIsViewNotes={setIsViewNotes}
                        selectedData={selectedData}
                        getDealListMutate={() => getDealListMutate({ searchQuery, filterType })}
                    />
                </SideModal>

                <FloorTabs>
                    <FloorTab
                        onClick={() => handleTabChange('1')}
                        $active={activeTab === "1"}
                    >
                        Cancelled ({summaryData?.cancelled || 0})
                    </FloorTab>
                    <FloorTab
                        onClick={() => handleTabChange('2')}
                        $active={activeTab === "2"}
                    >
                        Lost ({summaryData?.lost || 0})
                    </FloorTab>
                    <FloorTab
                        onClick={() => handleTabChange('3')}
                        $active={activeTab === "3"}
                    >
                        Leased End ({summaryData?.endOfLease || 0})
                    </FloorTab>
                </FloorTabs>

                <FilterTableWrapper>
                    <FilterWrapper>
                        <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                            <SearchFilterBox
                                options={searchOptions}
                                handleFilterChange={handleFilterChange}
                                handleClearFilters={handleClearFilter}
                            />
                            <DateRange
                                submitHandler={(values) => {
                                    const formattedStart = moment(values.startDate).format("YYYY-MM-DD");
                                    const formattedEnd = moment(values.endDate).format("YYYY-MM-DD");
                                    setStartDate(formattedStart);
                                    setEndDate(formattedEnd);
                                    getDealListMutate({
                                        searchQuery,
                                        filterType,
                                        startDate: formattedStart,
                                        endDate: formattedEnd,
                                    });
                                }}
                                defaultLabel={`${moment(startDate).format("DD MMM yyyy")} - ${moment(endDate).format("DD MMM yyyy")}`}
                            />
                        </div>
                    </FilterWrapper>

                    {filteredData && !isLoading && (
                        <TableWrapper>
                            <Table
                                apiData={filteredData}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                                activeTab={activeTab}
                                setIsEditDeal={setIsEditDeal}
                                setIsEditBasicInfo={setIsEditBasicInfo}
                                setIsViewNotes={setIsViewNotes}
                            />
                            {(!apiData || filteredData.length === 0) && <NotFoundModel />}
                        </TableWrapper>
                    )}
                </FilterTableWrapper>
                {error && !isLoading && <NotFoundModel />}
                {(isLoading || isRefetching) && <Loading />}
            </BoxContainer>
        </>
    );
};

const TableWrapper = tw.div`mt-4 bg-white rounded-md`;
const FloorTabs = tw.div`flex overflow-x-auto bg-white rounded-md my-4 border-b`;
const FloorTab = tw.button`
  ${(p) =>
        p.$active
            ? " text-primary-color border-b-2 border-b-primary-color bg-cyan-50 "
            : " text-gray-700 "} 
   px-5 py-3 cursor-pointer font-medium transition-all duration-200 whitespace-nowrap text-sm
`;

export default ClosedDeal;